import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { AddressService } from 'core/address/address.service';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { CreateContactResult } from 'src/app/modules/shared/entities/create-contact-result.class';
import { ContactData } from 'src/app/modules/shared/entities/contact-data.class';
import { Address } from 'src/app/modules/shared/entities/address.class';
import { Contact } from '../entities/contact.class';

@Injectable({
  providedIn: 'root'
})
export class ContactsService {
  // APIs
  private urlTypePhone = '/api/contact/templatevalues';
  private urlCreateContact = '/api/contact/create';
  private urlContactDetails = '/api/contact/get/';
  private urlContactEdit = '/api/contact/edit';
  private urlSaveNotes = '/api/contact/savenotes';
  private urlSavePhones = '/api/contact/savephones';
  private urlContactsList = '/api/contact/list';
  private urlDeleteContact = '/api/contact/delete';
  private urlPhotoSave = '/api/contactphoto/upload';
  private urlPhotoPurge = '/api/contactphoto/delete';
  private urlPhotoGet = '/api/contactphoto/get/';
  private usCountry = 'United States';

  private templateData: any;
  private templateDataState: any;
  private templateCountryData: any;
  private templateRegionData: any;
  private templateCountyData: any;

  private contactApiRequest: ContactData;

  constructor(
    public apiService: ApiService,
    private addressService: AddressService,
    public appInfoService: AppInfoService,
    public appStateService: AppStateService
  ) { }

  /**
   * Get template values
   * @param callbackParam - callback
   */
  getTemplateValues(callbackParam: (...args: any[]) => any) {
    if (this.templateData && this.templateCountryData && this.templateDataState && this.templateRegionData && this.templateCountyData) {
      callbackParam([
        this.templateData, this.templateCountryData,
        this.templateDataState, this.templateRegionData, this.templateCountyData
      ]);
      return;
    }

    const localCallback = ([templateCountryData, templateDataState, templateRegionData, templateCountyData, templateData]) => {
      this.templateCountryData = templateCountryData;
      this.templateDataState = templateDataState;
      this.templateData = templateData;
      this.templateRegionData = templateRegionData;
      this.templateCountyData = templateCountyData;
      callbackParam([
        this.templateData, this.templateCountryData,
        this.templateDataState, this.templateRegionData, this.templateCountyData
      ]);
    };

    this.addressService.getTemplateValues(localCallback, [this.urlTypePhone]);
  }

  /**
   * Creates new contact
   * @param customerId - Customer Id
   * @param leadId - Lead Id
   * @param profileValues - Profile values object
   * @param phoneValues - Phone numbers array
   * @param notes - Notes array
   * @param photos - Photos file list
   * @param callback - Callback
   */
  createContact(customerId: string, leadId: string, profileValues: Contact,
    phoneValues, notes, photos, isQuote, callback?: (...args: any[]) => any) {
    this.contactApiRequest = this.packProfileData(profileValues);
    this.contactApiRequest.customerId = customerId;
    this.contactApiRequest.leadId = leadId;
    this.contactApiRequest.isCreatedFromQuote = isQuote;

    this.apiService
      .post(this.urlCreateContact, this.contactApiRequest)
      .subscribe((result: CreateContactResult) => forkJoin([
        this.apiService.post(this.urlSaveNotes, { contactId: result.entityId, notes }),
        this.apiService.post(this.urlSavePhones, {
          contactId: result.entityId,
          phones: this.removeEmptyPhoneNumbers(phoneValues)
        }),
        this.uploadPhoto(result.entityId, photos)
      ]).subscribe(() => {
        this.checkForCallbackAndCallIt(callback);
      }));
  }

  /**
   * Uploads photo
   * @param contactId - Contact Id
   * @param photos - Photos file list
   * @returns - Response from post
   */
  private uploadPhoto(contactId: string, photos: any) {

    if (!photos.length) {
      return of({});
    }
    const file: File = photos[0];
    const formData: FormData = new FormData();
    formData.append('PhotoUpload', file, file.name);
    formData.append('id', contactId);
    return this.apiService.post(this.urlPhotoSave, formData);
  }

  /**
   * Returns response from getBlob
   * @param contactId - Contact Id
   * @returns - Response from getBlob
   */
  getPhoto(contactId: string) {
    return this.apiService.getBlob(this.urlPhotoGet + contactId);
  }

  /**
   * Returns response from post
   * @param contactId - Contact Id
   * @returns - Response from post
   */
  purgePhoto(contactId: string) {
    return this.apiService.post(this.urlPhotoPurge, { id: contactId });
  }

  /**
   * Get contact data
   * @param contactId - Contact Id
   * @param callback - Callback
   */
  getContactData(contactId: string, callback: (...args: any[]) => any) {
    this.apiService.get<Contact>(this.urlContactDetails + contactId).subscribe(value => callback(value));
  }

  /**
   * Updates contact info
   * @param customerId - Customer Id
   * @param leadId - Lead Id
   * @param profileValues - Profile values object
   * @param phoneValues - Phone numbers array
   * @param notes - Notes array
   * @param photos - Photos file list
   * @param callback - Callback
   */
  editContact(customerId: string, leadId: string, profileValues: Contact, phoneValues, notes, photos, callback?: (...args: any[]) => any) {
    this.contactApiRequest = this.packProfileData(profileValues);
    this.contactApiRequest.customerId = customerId;
    this.contactApiRequest.leadId = leadId;

    forkJoin([
      this.apiService.post(this.urlContactEdit, this.contactApiRequest),
      this.apiService.post(this.urlSaveNotes, { contactId: this.contactApiRequest.id, notes }),
      this.apiService.post(this.urlSavePhones, {
        contactId: this.contactApiRequest.id,
        phones: this.removeEmptyPhoneNumbers(phoneValues)
      }),
      this.uploadPhoto(this.contactApiRequest.id, photos)])
      .subscribe(() => {
        this.checkForCallbackAndCallIt(callback);
      });
  }

  /**
   * Gets contacts list
   * @param customerId - Customer Id
   * @param leadId - Lead Id
   * @param callback - Callback
   */
  getContactsList(customerId: string, leadId: string, callback?: (...args: any[]) => any) {
    this.apiService.post(this.urlContactsList, {
      page: 0,
      customerId,
      leadId
    }).subscribe((response: any[]) => {
      response.forEach((contact: Contact) => this.parseAddress(contact));
      if (callback) {
        callback(response);
      }
    });
  }

  /**
   * Deletes contact
   * @param contactId - Contact Id
   * @param callback - Callback
   */
  deleteContact(contactId: string, callback: (...args: any[]) => any) {
    this.apiService.post(this.urlDeleteContact, { id: contactId }).subscribe(() => callback());
  }

  /**
   * Packs profile data
   * @param profileValues - Profile values object
   * @returns - Packed profile data
   */
  private packProfileData(profileValues: Contact) {
    const profile = JSON.parse(JSON.stringify(profileValues));

    if (!profile.address.addressline1 &&
      !profile.address.addressline2 &&
      !profile.address.city &&
      !profile.address.countryId &&
      !profile.address.stateId &&
      !profile.address.zip) {
      profile.address = {
        addressline1: '',
        addressline2: '',
        city: '',
        countryId: 1,
        countyId: null,
        regionId: null,
        id: this.appInfoService.emptyGuid,
        province: null,
        stateId: null,
        zip: ''
      };
    } else {
      this.addressService.normalizeAddressStateAndZip(profile.address as Address);
    }

    profile.id = profile.id || this.appInfoService.emptyGuid;
    profile.sameAddressAsLead = profile.sameAddressAsLead || false;
    delete profile.phones;
    delete profile.notes;

    return profile;
  }

  /**
   * Parses and updates address
   * @param contact - Contact info object
   */
  private parseAddress(contact: Contact) {
    if (!contact.address) {
      return;
    }
    const addressArray = [];

    if (contact.address.addressline1) {
      addressArray.push(contact.address.addressline1);
    }
    if (contact.address.city) {
      addressArray.push(contact.address.city);
    }
    const state = contact.address.stateId ? this.templateDataState.find(st => st.stateId === contact.address.stateId) : '';
    if (state && state.stateName) {
      addressArray.push(state.stateName);
    } else {
      const province = contact.address.province.name ? contact.address.province.name : '';
      if (province) {
        addressArray.push(province);
      }
    }

    if (contact.address.zip) {
      addressArray.push(contact.address.zip);
    }
    if (contact.address.countryId) {
      addressArray.push(this.templateCountryData.Countries.find(country => country.Key === contact.address.countryId).Value);
    }
    contact.addressText = addressArray.join(', ');
  }

  /**
   * Removes empty phone numbers
   * @param phones - Phone numbers array
   * @returns - Valid phone numbers
   */
  private removeEmptyPhoneNumbers(phones: any) {
    return JSON.parse(JSON.stringify(phones)).filter(phone => phone.phoneNumber);
  }

  /**
   * Checks country
   * @param countryId - Country Id
   * @returns - True for US country Id
   */
  isUnitedStates(countryId: number) {
    return this.templateCountryData.Countries.find(country => country.Key === countryId).Value === this.usCountry;
  }

  /**
   * Check for callback and call it
   * @param callback - Callback
   */
  private checkForCallbackAndCallIt(callback?: (...args: any[]) => any) {
    if (callback) {
      callback();
    }
  }

  /**
   * Sets default contact photo
   * @returns - Urls object
   */
  displayDefaultContactPhoto() {
    const defaultPhotoUrl = this.appStateService.state.isElectron
      ? '/Program%20Files/Snapon%20IMPACT/snapon-impact-win32-x64/resources/app/assets/images/profile.jpeg'
      : '../../../../../assets/images/profile.jpeg';
    return { defaultPhotoUrl, photoUrl: defaultPhotoUrl };
  }
}
